import React, { useState, useEffect } from "react";
import { Edit, CirclePlus } from "lucide-react";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import { useNavigate } from "react-router-dom";

const INITIAL_PERMISSIONS = {
  productManagement: false,
  orderManagement: false,
  categoryManagement: false,
  couponsManagement: false,
  inventoryManagement: false,
  marketingManagement: false,
  userManagement: false,
};

const INITIAL_USER_STATE = {
  username: "",
  email: "",
  password: "",
  phoneNumber: "",
  permissions: INITIAL_PERMISSIONS,
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newUser, setNewUser] = useState(INITIAL_USER_STATE);
  const [editUser, setEditUser] = useState({
    ...INITIAL_USER_STATE,
    password: undefined,
  });
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/getallusers`);
      setUsers(response.data.filter((user) => !user.isDeleted));
    } catch (error) {
      console.error("Error fetching users:", error);
      // You might want to show an error toast or message here
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const checkPermissions = () => {
      const permissions = JSON.parse(
        localStorage.getItem("userPermissions") || "{}"
      );
      if (!permissions.userManagement) {
        navigate("/dashboard"); // Redirect if no permission
      }
    };

    checkPermissions();
  }, [navigate]);

  const handleCreateUser = async () => {
    const permissions = JSON.parse(
      localStorage.getItem("userPermissions") || "{}"
    );
    if (!permissions.userManagement) {
      setError("You don't have permission to create users");
      return;
    }

    try {
      const userData = {
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        phoneNumber: newUser.phoneNumber,
        permissions: newUser.permissions,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/add`,
        userData
      );

      if (response.data.message === "Admin user created successfully") {
        fetchUsers();
        setIsCreateModalOpen(false);
        setNewUser(INITIAL_USER_STATE);
        // Add success notification here if needed
      }
    } catch (error) {
      console.error(
        "Error creating user:",
        error.response?.data?.message || error.message
      );
      // Add error notification here if needed
    }
  };

  const handleEdit = (user) => {
    const permissions = JSON.parse(
      localStorage.getItem("userPermissions") || "{}"
    );
    if (!permissions.userManagement) {
      setError("You don't have permission to edit users");
      return;
    }

    setEditingId(user._id);
    setEditUser({
      username: user.username,
      email: user.email,
      phoneNumber: user.phoneNumber,
      permissions: { ...user.permissions },
      isActive: user.isActive,
    });
  };

  const handleSaveEdit = async (id) => {
    const permissions = JSON.parse(
      localStorage.getItem("userPermissions") || "{}"
    );
    if (!permissions.userManagement) {
      setError("You don't have permission to save user changes");
      return;
    }

    try {
      const response = await axios.put(`${API_BASE_URL}/api/admin/edit/${id}`, {
        username: editUser.username,
        permissions: editUser.permissions,
        isActive: editUser.isActive,
      });

      if (response.data.message === "Admin user updated successfully") {
        fetchUsers();
        setEditingId(null);
        // Add success notification here if needed
      }
    } catch (error) {
      console.error(
        "Error updating user:",
        error.response?.data?.message || error.message
      );
      // Add error notification here if needed
    }
  };

  const handleInputChange = (setter) => (e) => {
    const { name, value } = e.target;
    setter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (setter) => (permission) => {
    setter((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission],
      },
    }));
  };

  const handleDeleteUser = async () => {
    const permissions = JSON.parse(
      localStorage.getItem("userPermissions") || "{}"
    );
    if (!permissions.userManagement) {
      setError("You don't have permission to delete users");
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/edit/${userToDelete._id}`,
        {
          isDeleted: true,
        }
      );

      if (response.data.message === "Admin user updated successfully") {
        fetchUsers();
        setShowDeleteModal(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error(
        "Error deleting user:",
        error.response?.data?.message || error.message
      );
    }
  };

  const PermissionCheckbox = ({
    name,
    label,
    checked,
    onChange,
    disabled = false,
  }) => (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-4 h-4 rounded border-gray-300"
      />
      <span className="text-base">{label}</span>
    </div>
  );

  const UserInfoField = ({ icon, value }) => (
    <div className="flex items-center gap-4">
      <span className="text-xl text-gray-500">{icon}</span>
      <span className="text-base">{value}</span>
    </div>
  );

  const UserCard = ({ user }) => {
    const isEditing = editingId === user._id;
    const currentUser = isEditing ? editUser : user;

    const renderStatusBadge = () => (
      <div className="flex items-center gap-3">
        <div
          className={`w-3 h-3 rounded-full ${
            user.isActive
              ? "bg-green-500 ring-4 ring-green-100"
              : "bg-red-500 ring-4 ring-red-100"
          }`}
        />
        <span className="text-sm font-medium text-gray-700">
          {user.isActive ? "Active Account" : "Inactive Account"}
        </span>
      </div>
    );

    const renderUserInfo = () => {
      const fields = [
        { icon: "👤", value: currentUser.username, label: "Username" },
        { icon: "📞", value: currentUser.phoneNumber, label: "Phone Number" },
        { icon: "✉️", value: currentUser.email, label: "Email" },
      ];

      if (isEditing) {
        return (
          <div className="space-y-4">
            {fields.map(({ icon, label, value }) => (
              <div key={label} className="space-y-2">
                <div className="text-sm font-medium text-gray-700">{label}</div>
                <div className="border-2 border-gray-200 rounded-lg p-3 flex items-center gap-3 focus-within:border-purple-400 transition-colors">
                  <span className="text-xl">{icon}</span>
                  <input
                    type={label === "Email" ? "email" : "text"}
                    name={label.toLowerCase()}
                    value={value}
                    onChange={handleInputChange(setEditUser)}
                    className="flex-1 outline-none text-base"
                    placeholder={label}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {fields.map(({ icon, value, label }) => (
            <div key={label} className="space-y-1">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="flex items-center gap-3">
                <span className="text-xl">{icon}</span>
                <span className="text-sm text-gray-700">{value}</span>
              </div>
            </div>
          ))}
        </div>
      );
    };

    const renderPermissions = () => {
      if (isEditing) {
        return (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {Object.entries(INITIAL_PERMISSIONS).map(([key, _]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {key.replace("Management", "")}
                </span>
                <button
                  onClick={() => handlePermissionChange(setEditUser)(key)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    currentUser.permissions[key]
                      ? "bg-purple-600"
                      : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      currentUser.permissions[key]
                        ? "translate-x-5"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        );
      }

      const activePermissions = Object.entries(currentUser.permissions)
        .filter(([_, value]) => value)
        .map(([key]) => key);

      if (activePermissions.length === 0) {
        return (
          <div className="text-sm text-gray-500 italic">
            No active permissions
          </div>
        );
      }

      return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {activePermissions.map((key) => (
            <div key={key} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-600" />
              <span className="text-sm text-gray-600">
                {key.replace("Management", "")}
              </span>
            </div>
          ))}
        </div>
      );
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 w-full shadow-sm hover:shadow-md transition-shadow">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-start border-b border-gray-100 pb-4">
            {renderStatusBadge()}
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(user)}
                className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => {
                  setUserToDelete(user);
                  setShowDeleteModal(true);
                }}
                className="p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
              >
                <svg
                  className="w-4 h-4 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="space-y-4">{renderUserInfo()}</div>

          {/* Permissions */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700">Permissions</h3>
              {!isEditing && (
                <span className="text-xs text-gray-500">
                  {
                    Object.values(currentUser.permissions).filter(Boolean)
                      .length
                  }{" "}
                  Active
                </span>
              )}
            </div>
            {renderPermissions()}
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setEditingId(null)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveEdit(user._id)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm transition-colors"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CreateUserModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${
        isCreateModalOpen ? "" : "hidden"
      }`}
    >
      <div className="bg-white rounded-xl p-8 w-full max-w-4xl mx-4 shadow-2xl">
        <div className="flex justify-between items-center mb-8 border-b pb-4">
          <h2 className="text-2xl font-medium text-gray-800">
            Create New User
          </h2>
          <button
            onClick={() => setIsCreateModalOpen(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {[
              { label: "Username", icon: "👤", name: "username", type: "text" },
              { label: "Email", icon: "✉️", name: "email", type: "email" },
              {
                label: "Password",
                icon: "🔒",
                name: "password",
                type: "password",
              },
              {
                label: "Phone Number",
                icon: "📞",
                name: "phoneNumber",
                type: "text",
              },
            ].map((field) => (
              <div key={field.name}>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                </div>
                <div className="border-2 border-gray-200 rounded-lg p-3 flex items-center gap-3 focus-within:border-purple-400 transition-colors">
                  <span className="text-xl">{field.icon}</span>
                  <input
                    type={field.type}
                    name={field.name}
                    value={newUser[field.name]}
                    onChange={handleInputChange(setNewUser)}
                    className="flex-1 outline-none text-base"
                    placeholder={field.label}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Permissions
            </h3>
            {Object.entries(INITIAL_PERMISSIONS).map(([key, _]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  {key.replace("Management", " Management")}
                </span>
                <button
                  onClick={() => handlePermissionChange(setNewUser)(key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    newUser.permissions[key] ? "bg-purple-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      newUser.permissions[key]
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
          <button
            onClick={() => setIsCreateModalOpen(false)}
            className="px-6 py-2 border-2 border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateUser}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Create User
          </button>
        </div>
      </div>
    </div>
  );

  const DeleteConfirmationModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${
        showDeleteModal ? "" : "hidden"
      }`}
    >
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <h2 className="text-xl font-medium text-gray-800 mb-4">
          Confirm Delete
        </h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete user "{userToDelete?.username}"? This
          action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setUserToDelete(null);
            }}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteUser}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
          >
            Delete User
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">User Management</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 w-auto bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
        >
          <CirclePlus />
          Create User
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {users.map((user) => (
          <UserCard key={user._id} user={user} />
        ))}
      </div>

      <CreateUserModal />
      <DeleteConfirmationModal />
    </div>
  );
};

export default UserManagement;
